import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBooks, createBook, updateBook, deleteBook, reset } from '../../features/books/bookSlice';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../utils/cropImage';
import { useToast } from '../../contexts/ToastContext';
import { getImageUrl } from '../../utils/imageUrl';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BookManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const { books, isSuccess, isError, message } = useSelector((state) => state.books);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isbn: '',
    synopsis: '',
    publisher: '',
    publishedYear: '',
    category: '',
    price: ''
  });
  const [cover, setCover] = useState(null);
  const [demoPdf, setDemoPdf] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredBooks = books.filter(book => 
    book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.isbn.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Cropper states
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(3/4); // Book cover aspect ratio
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropperModalOpen, setIsCropperModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
    dispatch(getBooks());
  }, [dispatch, user, navigate]);

  useEffect(() => {
    if (isSuccess) {
      addToast({
        type: 'success',
        message: 'Buku berhasil disimpan!'
      });
      resetForm();
      dispatch(reset());
    }
    if (isError) {
      addToast({
        type: 'error',
        message: message || 'Gagal menyimpan buku!'
      });
      dispatch(reset());
    }
  }, [isSuccess, isError, message, dispatch, addToast]);

  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      isbn: '',
      synopsis: '',
      publisher: '',
      publishedYear: '',
      category: '',
      price: ''
    });
    setCover(null);
    setDemoPdf(null);
    setEditingId(null);
    setIsModalOpen(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setIsCropperModalOpen(false);
  };

  const onChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSynopsisChange = (value) => {
    setFormData((prev) => ({ ...prev, synopsis: value }));
  };

  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setImageSrc(URL.createObjectURL(file));
      setIsCropperModalOpen(true);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = useCallback(async (imageSrc, pixelCrop) => {
    const image = document.createElement('img');
    image.src = imageSrc;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        resolve(new File([file], 'cropped-image.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.95);
    });
  }, []);

  const applyCrop = async () => {
    if (imageSrc && croppedAreaPixels) {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCover(croppedFile);
      setIsCropperModalOpen(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    if (cover) data.append('cover', cover);
    if (demoPdf) data.append('demoPdf', demoPdf);

    if (editingId) {
      dispatch(updateBook({ id: editingId, bookData: data }));
    } else {
      dispatch(createBook(data));
    }
  };

  const onEdit = (book) => {
    setEditingId(book._id);
    setFormData({
      title: book.title,
      author: book.author,
      isbn: book.isbn,
      synopsis: book.synopsis,
      publisher: book.publisher,
      publishedYear: book.publishedYear,
      category: book.category,
      price: book.price
    });
    setCover(null); // Keep existing cover unless changed
    setDemoPdf(null);
    setImageSrc(null);
    setIsModalOpen(true);
  };

  const onDelete = (id) => {
    if (window.confirm('Yakin ingin menghapus buku ini?')) {
      dispatch(deleteBook(id));
    }
  };

  return (
    <div className="container mx-auto px-4">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Kelola Buku</h1>
          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Tambah Buku
          </button>
        </div>
        <div className="w-full md:w-1/3">
          <input
            type="text"
            placeholder="Cari buku..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Judul</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Penulis</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ISBN</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Harga</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredBooks.map((book) => (
              <tr key={book._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{book.title}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{book.author}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{book.isbn}</td>
                <td className="px-6 py-4 text-sm text-dark-blue-700 font-semibold">
                  Rp {book.price?.toLocaleString('id-ID') || 0}
                </td>
                <td className="px-6 py-4 text-sm flex gap-3">
                  <button 
                    onClick={() => onEdit(book)} 
                    className="text-dark-blue-600 hover:text-dark-blue-800 font-medium"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => onDelete(book._id)} 
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Hapus
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Main Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={resetForm}
        title={editingId ? 'Edit Buku' : 'Tambah Buku'}
      >
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Judul</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={onChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Penulis</label>
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={onChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ISBN</label>
              <input
                type="text"
                name="isbn"
                value={formData.isbn}
                onChange={onChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Harga (Rp)</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={onChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Penerbit</label>
              <input
                type="text"
                name="publisher"
                value={formData.publisher}
                onChange={onChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tahun Terbit</label>
              <input
                type="number"
                name="publishedYear"
                value={formData.publishedYear}
                onChange={onChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kategori</label>
            <input
              type="text"
              name="category"
              value={formData.category}
              onChange={onChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sinopsis</label>
            <ReactQuill
              theme="snow"
              value={formData.synopsis}
              onChange={onSynopsisChange}
              className="bg-gray-50 rounded-xl"
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  ['link', 'blockquote'],
                  ['clean']
                ]
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cover Buku</label>
            {cover ? (
              <div className="mb-4">
                <img 
                  src={URL.createObjectURL(cover)} 
                  alt="Cover Preview" 
                  className="w-40 h-53 object-cover rounded-xl border border-gray-200"
                />
                <button 
                  type="button" 
                  onClick={() => {
                    setCover(null);
                    setImageSrc(null);
                  }}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  Ganti Cover
                </button>
              </div>
            ) : editingId && books.find(b => b._id === editingId)?.cover ? (
              <div className="mb-4">
                <img 
                  src={getImageUrl(books.find(b => b._id === editingId).cover)} 
                  alt="Current Cover" 
                  className="w-40 h-53 object-cover rounded-xl border border-gray-200"
                />
                <button 
                  type="button" 
                  onClick={() => fileInputRef.current.click()}
                  className="mt-2 text-sm text-dark-blue-600 hover:text-dark-blue-800"
                >
                  Ganti Cover
                </button>
              </div>
            ) : (
              <div className="mb-4">
                <p className="text-gray-500 text-sm">Belum ada cover</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Demo PDF</label>
            {demoPdf ? (
              <div className="mb-4">
                <p className="text-sm text-gray-700">{demoPdf.name}</p>
                <button 
                  type="button" 
                  onClick={() => setDemoPdf(null)}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  Hapus PDF
                </button>
              </div>
            ) : editingId && books.find(b => b._id === editingId)?.demoPdf ? (
              <div className="mb-4">
                <a 
                  href={getImageUrl(books.find(b => b._id === editingId).demoPdf)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-dark-blue-600 hover:text-dark-blue-800"
                >
                  Lihat PDF Saat Ini
                </a>
                <br />
                <button 
                  type="button" 
                  onClick={() => document.getElementById('demoPdfInput').click()}
                  className="mt-2 text-sm text-dark-blue-600 hover:text-dark-blue-800"
                >
                  Ganti PDF
                </button>
              </div>
            ) : (
              <div className="mb-4">
                <p className="text-gray-500 text-sm">Belum ada demo PDF</p>
              </div>
            )}
            <input
              id="demoPdfInput"
              type="file"
              accept="application/pdf"
              onChange={(e) => e.target.files?.[0] && setDemoPdf(e.target.files[0])}
              className="w-full"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={resetForm}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors"
            >
              {editingId ? 'Update' : 'Simpan'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Cropper Modal */}
      <Modal
        isOpen={isCropperModalOpen}
        onClose={() => setIsCropperModalOpen(false)}
        title="Potong Cover Buku"
      >
        <div className="relative h-96 bg-black rounded-xl overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          )}
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Zoom</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => setIsCropperModalOpen(false)}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={applyCrop}
            className="flex-1 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors"
          >
            Terapkan
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BookManagement;
